import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@app/components/Button/Button';
import {
  getBusinessesAdmin,
  getBusinessAdmin,
  updateBusinessAdmin,
  changeBusinessPlan,
  getAllPlansAdmin,
  exportBusinessEmails,
} from '@app/services/admin-business';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { InputField } from '@app/components/Fields/InputField';
import { CheckboxField } from '@app/components/Fields/CheckboxField';
import { PhoneField } from '@app/components/Fields/PhoneField';
import { Tabs } from '@app/components/Tabs/Tabs';
import { AdminBusinessInterface, PlanInterface } from '@app/models/business';
import { useDebounce } from '@app/utils/use-debounce';
import { ListResponse, UserStatus } from '@app/models';
import { Select } from '@app/components/Select/Select.tsx';
import { OrderDir, OrderDirType } from '@app/constants/core.ts';

const PlanBadge = ({ planType }: { planType?: string | null }) => {
  const planStyles: Record<string, string> = {
    // BASIC: 'bg-gray-100 text-gray-800',
    // ESSENTIAL: 'bg-blue-100 text-blue-800',
    // PROFESSIONAL: 'bg-purple-100 text-purple-800',
    // ENTERPRISE: 'bg-yellow-100 text-yellow-800',
    FREE: 'bg-gray-100 text-gray-800',
    PRO: 'bg-purple-100 text-purple-800',
    GROWTH: 'bg-yellow-100 text-yellow-800',
  };

  if (!planType) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        No Plan
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        planStyles[planType] || 'bg-gray-100 text-gray-800'
      }`}
    >
      {planType}
    </span>
  );
};

const StatusBadge = ({ business }: { business: AdminBusinessInterface }) => {
  const owner = business.members?.find((m) => m.isOwner);
  const status = owner?.user?.status as UserStatus | undefined;

  if (!status) {
    return '-';
  }

  const statusStyles = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    // SUSPENDED: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

const businessTableHeaders = [
  { label: 'Business', field: 'name', sortable: true },
  { label: 'Owner', field: 'owner', sortable: true },
  { label: 'Signip Date', field: 'createdAt', sortable: true },
  { label: 'Plan', field: 'plan', sortable: true },
  // { label: 'Category', field: 'category', sortable: true },
  { label: 'Google integration', field: 'integrationGoogle', sortable: true },
  { label: 'Review requests', field: 'reviewRequests', sortable: true },
  { label: 'Last Active', field: 'lastActiveAt', sortable: false },
  { label: 'Activation Status', field: 'activationStatus', sortable: false },
  { label: 'Actions', field: 'actions', sortable: false },
];

const ACTIVITED_FILTER_OPTIONS = [
  { value: 'activated', label: 'Activated' },
  { value: 'not_activated', label: 'Not activated' },
];

interface AdminBusinessResponse extends ListResponse<AdminBusinessInterface> {}

interface FormType {
  orderBy: string;
  orderDir: OrderDirType;
  page: number;
  perPage: number;
  search: string;
  activatedFilter?: string;
  userActivity?: string;
  subscriptionStatus?: string;
}

export const AdminBusinesses = () => {
  const navigate = useNavigate();
  // const [currentPage, setCurrentPage] = useState(1);
  // const [perPage, setPerPage] = useState(10);
  // const [searchTerm, setSearchTerm] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<AdminBusinessInterface | null>(null);
  const [modalType, setModalType] = useState<'edit' | 'plan' | null>(null);
  const queryClient = useQueryClient();

  const form = useForm<FormType>({
    defaultValues: {
      orderBy: 'createdAt',
      orderDir: OrderDir.desc,
      page: 1,
      perPage: 10,
      search: '',
      activatedFilter: undefined,
      userActivity: undefined,
      subscriptionStatus: undefined,
    },
  });

  const {
    orderBy,
    orderDir,
    page,
    perPage,
    search,
    activatedFilter,
    userActivity,
    subscriptionStatus,
  } = form.watch();

  const debouncedSearch = useDebounce(search);

  const {
    data: businessesResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'businesses',
      'admin',
      {
        page,
        perPage,
        orderDir,
        orderBy,
        search: debouncedSearch,
        activatedFilter,
        userActivity,
        subscriptionStatus,
      },
    ],
    queryFn: () =>
      getBusinessesAdmin(
        page,
        perPage,
        debouncedSearch.length > 2 ? debouncedSearch : undefined,
        // debouncedSearch ? debouncedSearch : undefined,
        {
          ...(activatedFilter && { activatedFilter }),
          ...(userActivity && { userActivity }),
          ...(subscriptionStatus && { subscriptionStatus }),
        }
      ),
    placeholderData: keepPreviousData,
  });

  // const debouncedSearchTerm = useDebounce(searchTerm, 500);
  //
  // const {
  //   data: businessesResponse,
  //   isLoading,
  //   error,
  // } = useQuery({
  //   queryKey: ['businesses', 'admin', currentPage, debouncedSearchTerm],
  //   queryFn: () =>
  //     getBusinessesAdmin(
  //       currentPage,
  //       perPage,
  //       debouncedSearchTerm.length > 2 ? debouncedSearchTerm : undefined
  //     ),
  //   placeholderData: keepPreviousData,
  // });
  //
  // useEffect(() => {
  //   if (debouncedSearchTerm.length > 2) {
  //     setCurrentPage(1);
  //   }
  // }, [debouncedSearchTerm]);



  const totalPages = useMemo(() => {
    if (!businessesResponse) return 0;
    const responseData = businessesResponse as AdminBusinessResponse;
    return Math.ceil(responseData.total / responseData.perPage);
  }, [businessesResponse]);

  const handlePrevPage = () => {
    if (page > 1) {
      form.setValue('page', page - 1);
    }
  };

  const handleNextPage = () => {
    if (businessesResponse && page < totalPages) {
      form.setValue('page', page + 1);
    }
  };

  const handleLimitChange = (limit?: number) => {
    if (limit) {
      form.setValue('perPage', limit);
      form.setValue('page', 1);
    }
  };

  // const handlePageChange = (page: number) => {
  //   setCurrentPage(page);
  // };


  const handleActivatedFilterSelect = (selectedValue?: string) => {
    form.setValue('activatedFilter', selectedValue || undefined);
    form.setValue('page', 1);
  };

  const handleUserActivitySelect = (selectedValue?: string) => {
    form.setValue('userActivity', selectedValue || undefined);
    form.setValue('page', 1);
  };

  const handleSubscriptionStatusSelect = (selectedValue?: string) => {
    form.setValue('subscriptionStatus', selectedValue || undefined);
    form.setValue('page', 1);
  };

  const exportEmailsMutation = useMutation({
    mutationFn: () =>
      exportBusinessEmails(
        debouncedSearch.length > 2 ? debouncedSearch : undefined,
        {
          ...(activatedFilter && { activatedFilter }),
          ...(userActivity && { userActivity }),
          ...(subscriptionStatus && { subscriptionStatus }),
        }
      ),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `business-emails-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Emails exported successfully!');
    },
    onError: () => {
      toast.error('Failed to export emails. Please try again.');
    },
  });

  const openModal = async (
    business: AdminBusinessInterface,
    type: 'edit' | 'plan'
  ) => {
    try {
      const fullBusiness = await getBusinessAdmin(business.id);
      setSelectedBusiness(fullBusiness);
      setModalType(type);
    } catch {
      toast.error('Failed to load business details');
    }
  };

  const closeModal = () => {
    setSelectedBusiness(null);
    setModalType(null);
  };

  const renderBusinessRow = (businesses: AdminBusinessInterface[]) => {
    return businesses.map((business: AdminBusinessInterface) => (
      <tr key={business.id}>
        <td className="px-6 py-4 whitespace-nowrap">
          <div>
            <div className="text-sm font-medium text-gray-900">
              {business.name}
            </div>
            <div className="text-sm text-gray-500">{business.email}</div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {(() => {
            const owner = business.members?.find((m) => m.isOwner);
            return (
              <>
                <div className="text-sm text-gray-900">
                  {owner?.user?.firstName} {owner?.user?.lastName}
                </div>
                <div className="text-sm text-gray-500">
                  {owner?.user?.email}
                </div>
              </>
            );
          })()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {format(new Date(business.createdAt), 'dd-MM-yyyy')}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <PlanBadge planType={business.subscription?.plan?.type} />
        </td>
        {/*<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">*/}
        {/*  {business.category?.name || '-'}*/}
        {/*</td>*/}
        <td
          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
          title={
            business.googleIntegrationDate
              ? format(new Date(business.googleIntegrationDate), 'PPpp')
              : undefined
          }
        >
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${business.googleIntegrationDate ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
          >
            {business.googleIntegrationDate ? 'Yes' : 'No'}
          </span>
        </td>
        <td
          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
          title={
            business.lastReviewRequestDate
              ? format(new Date(business.lastReviewRequestDate), 'PPpp')
              : undefined
          }
        >
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${business.lastReviewRequestCount ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
          >
            {(business.lastReviewRequestCount ?? 0) > 0 ? business.lastReviewRequestCount : 'No'}
          </span>
        </td>
        <td
          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
          title={
            business.lastActiveAt
              ? format(new Date(business.lastActiveAt), 'PPpp')
              : 'Never been active'
          }
        >
          {business.lastActiveAt
            ? formatDistanceToNow(new Date(business.lastActiveAt), {
                addSuffix: true,
              })
            : 'Never'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <StatusBadge business={business} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <Menu>
            <MenuButton>
              <Button
                kind="secondary"
                size="small"
                className="text-gray-600 hover:text-gray-900"
              >
                Actions
              </Button>
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom end"
              className="w-48 p-1 bg-white shadow-lg rounded-md border border-gray-200 transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-10"
            >
              <MenuItem>
                <button
                  onClick={() => openModal(business, 'edit')}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Edit Business
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() =>
                    navigate(`/admin/businesses/${business.id}/members`)
                  }
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Manage Members
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => openModal(business, 'plan')}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Change Plan
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </td>
      </tr>
    ));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading businesses...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">
          Error loading businesses. Please try again.
        </div>
      </div>
    );
  }

  const businesses = businessesResponse?.data || [];
  const total = businessesResponse?.total || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Business Management
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage businesses, owners, members, and plans
        </p>
      </div>

      {/* Search Bar */}

      <div className="bg-white p-2 space-y-4 shadow rounded-lg">
        <div
          className="flex flex-row gap-4 flex-wrap align-items-center"
          style={{ alignItems: 'center' }}
        >
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search businesses by name or email..."
              value={search}
              onChange={(e) => form.setValue('search', e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-gray-300"
            />
          </div>
          <Select
            options={ACTIVITED_FILTER_OPTIONS}
            onChange={(value) => handleActivatedFilterSelect(value)}
            value={activatedFilter || ''}
            name="activatedFilter"
            placeholder="All"
          />
          <Select
            options={[
              { value: 'new_signups', label: 'New signups' },
              { value: 'inactive_7_days', label: 'Inactive for 7+ days' },
            ]}
            onChange={(value) => handleUserActivitySelect(value)}
            value={userActivity || ''}
            name="userActivity"
            placeholder="All user activity"
          />
          <Select
            options={[
              { value: 'trial', label: 'Trial' },
              { value: 'paid', label: 'Paid' },
              { value: 'canceled', label: 'Canceled' },
            ]}
            onChange={(value) => handleSubscriptionStatusSelect(value)}
            value={subscriptionStatus || ''}
            name="subscriptionStatus"
            placeholder="All subscriptions"
          />
          <Button
            kind="secondary"
            size="small"
            onClick={() => exportEmailsMutation.mutate()}
            loading={exportEmailsMutation.isPending}
            disabled={exportEmailsMutation.isPending || businesses.length === 0}
          >
            Export Emails
          </Button>
        </div>
      </div>
      {/*<div className="bg-white p-2 shadow rounded-lg">*/}
      {/*  <div className="flex-1 max-w-md">*/}
      {/*    <input*/}
      {/*      type="text"*/}
      {/*      placeholder="Search businesses by name or email..."*/}
      {/*      value={searchTerm}*/}
      {/*      onChange={(e) => setSearchTerm(e.target.value)}*/}
      {/*      className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-gray-300"*/}
      {/*    />*/}
      {/*  </div>*/}

      {/*  */}
      {/*</div>*/}

      {/* Businesses Table */}
      <div className="bg-white shadow overflow-auto sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Businesses ({total} total)
            </h3>
          </div>

          {businesses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No businesses found</p>
            </div>
          ) : (
            <div className="overflow-auto bg-white">
              <GenericTable<AdminBusinessInterface>
                data={businesses}
                headers={businessTableHeaders}
                renderRow={renderBusinessRow}
                currentPage={page}
                isLoading={isLoading}
                pagination={{
                  total,
                  handleNextPage,
                  handlePrevPage,
                  handleLimitChange,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedBusiness && modalType === 'edit' && (
        <EditBusinessModal
          business={selectedBusiness}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['businesses', 'admin'],
            });
            closeModal();
          }}
        />
      )}

      {selectedBusiness && modalType === 'plan' && (
        <ChangePlanModal
          business={selectedBusiness}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['businesses', 'admin'],
            });
            closeModal();
          }}
        />
      )}
    </div>
  );
};

// Edit Business Modal
interface EditBusinessModalProps {
  business: AdminBusinessInterface;
  onClose: () => void;
  onSuccess: () => void;
}

const EditBusinessModal = ({
  business,
  onClose,
  onSuccess,
}: EditBusinessModalProps) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('details');

  const editForm = useForm({
    defaultValues: {
      name: business.name || '',
      email: business.email || '',
      phoneNumber: business.phoneNumber || '',
      address: business.address || '',
      description: business.description || '',
      websiteUrl: business.websiteUrl || '',
      reviewRequestLimit: business.subscription?.reviewRequestLimit || 0,
      seatLimit: business.subscription?.seatLimit || 0,
      aiEnabled: business.subscription?.aiEnabled || false,
      automationEnabled: business.subscription?.automationEnabled || false,
      messagingEnabled: business.subscription?.messagingEnabled || false,
      note: business.note?.content || undefined,
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AdminBusinessInterface>) =>
      updateBusinessAdmin(business.id, data),
    onSuccess: () => {
      toast.success('Business updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      onSuccess();
    },
    onError: (error: any) => {
      if (error?.data?.message) {
        toast.error(error.data.message);
      } else {
        toast.error('Failed to update business. Please try again.');
      }
    },
  });

  const onSubmit = (values: any) => {
    updateMutation.mutate(values);
  };

  const tabs = [
    { label: 'Business Details', value: 'details' },
    { label: 'Subscription & Settings', value: 'subscription' },
    { label: 'Notes', value: 'notes' },
  ];

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="space-y-4 border bg-white w-full max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto">
          <FormProvider {...editForm}>
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Business
              </h3>

              <Tabs
                tabs={tabs}
                onClick={(value) => setActiveTab(value)}
                defaultValue="details"
              />

              <form
                onSubmit={editForm.handleSubmit(onSubmit)}
                className="space-y-4 mt-6"
              >
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputField
                        name="name"
                        label="Business Name"
                        type="text"
                      />
                      <InputField name="email" label="Email" type="email" />
                    </div>

                    <PhoneField name="phoneNumber" label="Phone number" />

                    <InputField name="address" label="Address" type="text" />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        {...editForm.register('description')}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>

                    <InputField
                      name="websiteUrl"
                      label="Website URL"
                      type="url"
                    />
                  </div>
                )}

                {activeTab === 'subscription' && (
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4">
                      Current Plan
                    </h4>

                    {business.subscription?.plan ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600">
                              Plan Type
                            </p>
                            <p className="text-lg font-semibold text-gray-900 capitalize">
                              {business.subscription.plan.type}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">
                              Price
                            </p>
                            <p className="text-lg font-semibold text-gray-900">
                              ${business.subscription.plan.price}/
                              {business.subscription.plan.currency}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">
                              Review Request Limit
                            </p>
                            <p className="text-lg font-semibold text-gray-900">
                              {business.subscription.plan.reviewRequestLimit.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">
                              Seat Limit
                            </p>
                            <p className="text-lg font-semibold text-gray-900">
                              {business.subscription.plan.seatLimit.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {business.subscription.plan.features?.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-600 mb-2">
                              Features
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {business.subscription.plan.features.map(
                                (feature) => (
                                  <span
                                    key={feature.id}
                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                                  >
                                    {feature.name}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-gray-500">No active plan</p>
                      </div>
                    )}

                    <h4 className="text-md font-medium text-gray-900 mb-4 mt-6">
                      Usage Limits
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="font-medium text-sm inline-block mb-1">
                          Review Request Limit
                        </label>
                        <input
                          {...editForm.register('reviewRequestLimit')}
                          type="number"
                          disabled
                          className="bg-gray-100 border border-gray-300 rounded py-3 px-4 w-full text-gray-700 font-medium cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="font-medium text-sm inline-block mb-1">
                          Seat Limit
                        </label>
                        <input
                          {...editForm.register('seatLimit')}
                          type="number"
                          disabled
                          className="bg-gray-100 border border-gray-300 rounded py-3 px-4 w-full text-gray-700 font-medium cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Feature Settings
                      </h4>

                      <div className="space-y-3 mt-4">
                        <div className="flex items-center space-x-3">
                          <CheckboxField name="aiEnabled" />
                          <label className="text-sm font-semibold text-gray-800 cursor-pointer">
                            AI Enabled
                          </label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <CheckboxField name="automationEnabled" />
                          <label className="text-sm font-semibold text-gray-800 cursor-pointer">
                            Automation Enabled
                          </label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <CheckboxField name="messagingEnabled" />
                          <label className="text-sm font-semibold text-gray-800 cursor-pointer">
                            Messaging Enabled
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'notes' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Admin Notes
                        </label>
                        <textarea
                            {...editForm.register('note')}
                            rows={10}
                            placeholder="Add internal notes about this business..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={onClose}
                    kind="secondary"
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    kind="primary"
                    loading={updateMutation.isPending}
                    disabled={updateMutation.isPending}
                  >
                    Update Business
                  </Button>
                </div>
              </form>
            </div>
          </FormProvider>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

interface ChangePlanModalProps {
  business: AdminBusinessInterface;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangePlanModal = ({
  business,
  onClose,
  onSuccess,
}: ChangePlanModalProps) => {
  const queryClient = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(
    business.subscription?.planId || null
  );

  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: getAllPlansAdmin,
  });

  const changePlanMutation = useMutation({
    mutationFn: (planId: number) => changeBusinessPlan(business.id, planId),
    onSuccess: () => {
      toast.success('Plan changed successfully!');
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      onSuccess();
    },
    onError: () => {
      toast.error('Failed to change plan');
    },
  });

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="space-y-4 border bg-white w-full max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
          <div className="p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Change Plan - {business.name}</h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-600">Current Plan:</p>
              <p className="text-sm font-medium">{business.subscription?.plan?.type || 'No Plan'}</p>
            </div>

            {isLoadingPlans ? (
              <p className="text-sm text-gray-500">Loading plans...</p>
            ) : (
              <div className="space-y-2">
                {plans?.map((plan: PlanInterface) => (
                  <label
                    key={plan.id}
                    className={`flex items-center justify-between p-3 border rounded-md cursor-pointer ${
                      selectedPlanId === plan.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <input type="radio" name="plan" value={plan.id} checked={selectedPlanId === plan.id} onChange={() => setSelectedPlanId(plan.id)} className="mr-3" />
                      <div>
                        <p className="text-sm font-medium">{plan.type}</p>
                        <p className="text-xs text-gray-500">
                          {plan.reviewRequestLimit === null ? 'Unlimited' : plan.reviewRequestLimit} requests,
                          {plan.aiReplyLimit === null ? 'Unlimited' : plan.aiReplyLimit} AI Replies,
                          {/*{plan.contactUploadLimit === null ? 'Unlimited' : plan.contactUploadLimit} customers import,*/}
                          {plan.seatLimit} seats
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium">
                      ${plan.price}/{plan.currency}
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" onClick={onClose} kind="secondary" disabled={changePlanMutation.isPending}>
                Cancel
              </Button>
              <Button
                type="button"
                kind="primary"
                onClick={() => selectedPlanId && changePlanMutation.mutate(selectedPlanId)}
                loading={changePlanMutation.isPending}
                disabled={!selectedPlanId || selectedPlanId === business.subscription?.planId || changePlanMutation.isPending}
              >
                Change Plan
              </Button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
